import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import { ChevronRight, LogOut, Sun, Moon } from "lucide-react";
import LogoLight from "../../../assets/images/icons/logo-white.svg";
import LogoDark from "../../../assets/images/icons/logo-dark.svg";
import ShortLogo from "../../../assets/images/icons/shortLogo.svg";
import { useTheme } from "../../../shared/hooks/useTheme";
import { sideMenu } from "../../../config/sideMenu";
import { endSession } from "../../auth/login/actions";
import { useUserRole } from "../../../shared/hooks/useUserRole";
import { SideMenuItem } from "../../../interfaces/config/sideMenu";

interface SideMenuProps {
  onLinkClick: () => void;
  isCollapsed?: boolean;
}

const SideMenu: React.FC<SideMenuProps> = ({
  onLinkClick,
  isCollapsed = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleTheme, isDark } = useTheme();
  const [activeRoute, setActiveRoute] = useState(location.pathname);
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>({
    4: true,
  });
  const userRole = useUserRole();

  // Update active route when location changes
  useEffect(() => {
    setActiveRoute(location.pathname);
  }, [location.pathname]);

  const handleMenuItemClick = (
    route: string,
    hasChildren = false,
    itemId: number | null = null,
  ) => {
    if (hasChildren && itemId) {
      if (isCollapsed) {
        const menuItem = sideMenu.find((item) => item.id === itemId);
        if (menuItem && menuItem.children && menuItem.children.length > 0) {
          const firstChildRoute = menuItem.children[0].route;
          setActiveRoute(firstChildRoute);
          onLinkClick();
          navigate(firstChildRoute);
        }
        return;
      }

      setExpandedItems((prev) => ({
        ...prev,
        [itemId]: !prev[itemId],
      }));
      return;
    }

    setActiveRoute(route);
    onLinkClick();
    navigate(route);
  };

  const getStatusColor = (status: string | undefined) => {
    switch (status) {
      case "active":
        return "text-blue-500";
      case "done":
        return "text-green-500";
      case "hold":
        return "text-red-500";
      default:
        return "text-gray-500";
    }
  };

  const filteredMenu = sideMenu.filter((item: SideMenuItem) => {
    if (!userRole || userRole === "superadmin") {
      return true;
    }

    const rules = {
      pharmacist: ["Dashboard", "Pharmacy"],
      doctor: [
        "Dashboard",
        "Patients",
        "Q&A",
        "Medora360MD",
        "Drugs & Diseases",
        "Congresses & Medical Updates",
      ],
      accountant: ["Dashboard", "Billing & Claims", "Settings"],
    };

    return rules[userRole]?.includes(item.label) ?? false;
  });

  return (
    <div
      className={`flex flex-col h-full bg-white dark:bg-black transition-all duration-300 ease-in-out ${isCollapsed ? "w-16" : "w-72"}`}
    >
      {/* Logo Section */}
      <div
        className={`flex items-center ${isCollapsed ? "justify-center" : "justify-start"} px-4 py-6 h-[64px] flex-shrink-0`}
      >
        <img
          src={isCollapsed ? ShortLogo : isDark ? LogoDark : LogoLight}
          alt="Logo"
          className={`h-8 w-auto transition-all duration-300 ${isCollapsed ? "w-8" : ""}`}
        />
      </div>

      {/* Navigation - scrollable */}
      <div className="flex-1 min-h-0 px-2 pb-6 overflow-y-auto scrollbar-hide">
        <nav className="space-y-1">
          {filteredMenu.map((item) => {
            const IconComponent = item.icon;
            const hasChildren = item.children && item.children.length > 0;
            const isChildActive =
              hasChildren &&
              item.children?.some((child) => activeRoute === child.route);
            const isActive =
              !hasChildren &&
              (item.matchChildRoutes
                ? activeRoute.startsWith(item.route)
                : activeRoute === item.route);
            const isExpanded = expandedItems[item.id];
            const isSelected = isActive || (isCollapsed && isChildActive);

            return (
              <div key={item.id}>
                <button
                  onClick={() =>
                    handleMenuItemClick(item.route, hasChildren, item.id)
                  }
                  className={`
                    w-full flex items-center px-2 py-2 text-left rounded-lg transition-all duration-300 text-sm group
                    ${
                      isSelected
                        ? "bg-primary text-white"
                        : "text-gray-600 dark:text-gray-400 hover:bg-gray-400/40 hover:text-gray-800"
                    }
                  `}
                >
                  <div className="flex items-center w-full">
                    <div
                      className={`min-w-[32px] h-8 flex items-center justify-center group-hover:scale-110 transition-all duration-300 ${isSelected ? "bg-white/20 rounded" : ""}`}
                    >
                      <IconComponent
                        size={16}
                        className={isSelected ? "text-white" : "text-gray-500"}
                      />
                    </div>
                    <span
                      className={`ml-2 transition-all duration-300 whitespace-nowrap ${isCollapsed ? "opacity-0 w-0" : "opacity-100 w-auto"}`}
                    >
                      {item.label}
                    </span>
                    {hasChildren && (
                      <ChevronRight
                        size={14}
                        className={`ml-auto transform transition-all duration-300 ${isExpanded ? "rotate-90" : ""} ${isCollapsed ? "opacity-0 w-0" : "opacity-100 w-auto"}`}
                      />
                    )}
                  </div>
                </button>

                {hasChildren && (
                  <div
                    className={`
                    ml-3 mt-1 space-y-1 overflow-hidden transition-all duration-300 ease-in-out
                    ${isExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}
                    ${isCollapsed ? "hidden" : ""}
                  `}
                  >
                    {item.children?.map((child) => {
                      const ChildIcon = child.icon;
                      const isChildSelected = activeRoute === child.route;
                      return (
                        <button
                          key={child.id}
                          onClick={() => handleMenuItemClick(child.route)}
                          className={`
                            w-full flex items-center px-2 py-2 text-left rounded-lg transition-all duration-300 text-sm group
                            ${
                              isChildSelected
                                ? "bg-primary text-white"
                                : "text-gray-600 dark:text-gray-400 hover:bg-gray-400/40 hover:text-gray-800"
                            }
                          `}
                        >
                          <div className="flex items-center w-full">
                            <div
                              className={`min-w-[32px] h-8 flex items-center justify-center group-hover:scale-110 transition-all duration-300 ${isChildSelected ? "bg-white/20 rounded" : ""}`}
                            >
                              <ChildIcon
                                size={16}
                                className={
                                  isChildSelected
                                    ? "text-white"
                                    : getStatusColor(child.status)
                                }
                              />
                            </div>
                            <span className="ml-2 whitespace-nowrap">
                              {child.label}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      {/* Bottom Section */}
      <div className="border-t border-gray-100 dark:border-gray-900 px-2 py-4 flex-shrink-0">
        <div className="space-y-1">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="w-full flex items-center px-2 py-2 text-left rounded-lg transition-all duration-300 text-sm group text-gray-600 dark:text-gray-400 hover:bg-gray-400/40 hover:text-gray-800"
          >
            <div className="flex items-center w-full">
              <div className="min-w-[32px] h-8 flex items-center justify-center group-hover:scale-110 transition-all duration-300">
                {isDark ? <Sun size={16} /> : <Moon size={16} />}
              </div>
              <span
                className={`ml-2 transition-all duration-300 whitespace-nowrap ${isCollapsed ? "opacity-0 w-0" : "opacity-100 w-auto"}`}
              >
                {isDark ? "Light Mode" : "Dark Mode"}
              </span>
            </div>
          </button>
          {/* Logout */}
          <button
            onClick={() => {
              endSession();
            }}
            className="w-full flex items-center px-2 py-2 text-left rounded-lg transition-all duration-300 text-sm group text-gray-600 dark:text-gray-400 hover:bg-gray-400/40 hover:text-gray-800"
          >
            <div className="flex items-center w-full">
              <div className="min-w-[32px] h-8 flex items-center justify-center group-hover:scale-110 transition-all duration-300">
                <LogOut size={16} />
              </div>
              <span
                className={`ml-2 transition-all duration-300 whitespace-nowrap ${isCollapsed ? "opacity-0 w-0" : "opacity-100 w-auto"}`}
              >
                Logout
              </span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SideMenu;
